import { useCallback, useEffect, useState } from "react";
import AdminUnlockPresenter from "./AdminUnlockPresenter";
import {
  ADMINUNLOCK_MODEL,
  OtpRequest,
  OtpResponse,
} from "api/interfaces/helpInterface.interface";
import { postGenerateOTP } from "api/helpAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import useStatusMutation from "hook/useStatusMutation";
import { CustomError } from "api/interfaces/commonInterface.interface";
import useApiError from "hook/useApiError";
import { handleCopyClipBoard } from "utils/functions";
import { checkMacAddressRegex } from "utils/regEx";
import {
  getAccountTimeFormatDate,
  getTodayToUTCDate,
  isValidTimeInput,
} from "utils/timeUtil";
import { FIRMWARE_FEATURE_KEY } from "api/interfaces/managerInterface.interface";
import { postFirmwareVersionInfo } from "api/manageAPI";
import { useQuery } from "react-query";
import { RecorderType } from "utils/RecorderUtil";

const initialFormData: OtpRequest = {
  device: "COVA",
  model: ADMINUNLOCK_MODEL.N0A,
  macAddress: "",
  expireDate: "",
};

export default function AdminUnlockContainer(): JSX.Element {
  const { handleErrorNotify, notifySuccess, lang, intlString } = useApiError();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [formData, setFormData] = useState<OtpRequest>({ ...initialFormData });
  const [time, setTime] = useState<string>("");

  const [otp, setOtp] = useState<string>("");

  const [validateMsg, setValidateMsg] = useState<Record<string, string>>({
    macAddress: "",
    time: "",
  });

  const adminUnlockVersion = useQuery(
    ["adminUnlockVersion", formData.model, formData.device],
    () =>
      postFirmwareVersionInfo({
        featureKey: FIRMWARE_FEATURE_KEY.ADMIN_UNLOCK,
        displayType: formData.device as RecorderType,
        model: formData.model,
      }),
    {
      enabled: !!formData.model && !!formData.device,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );
  const mutationGenerateOTP = useStatusMutation(postGenerateOTP, {
    onSuccess: (res: OtpResponse) => {
      if (res.result && res.error === 0) {
        onInitialValidate();
        setOtp(res.result);
        notifySuccess(lang.validateMsg.help.utility.admin.generate.success);
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.validateMsg.help.utility.admin.generate.fail);
    },
  });
  const onInitialValidate = () => {
    setValidateMsg((msg) => {
      return {
        ...msg,
        macAddress: "",
        time: "",
      };
    });
  };

  const onChange = useCallback(
    ({ name, value }: { name: string; value: string }) => {
      onInitialValidate();
      if (name === "device") {
        setOtp("");
      }
      if (name === "collapsed") {
        setIsCollapsed(!isCollapsed);
        return;
      }
      if (name === "time") {
        setTime(value);
        return;
      }

      setFormData((form) => {
        return {
          ...form,
          [name]: value,
        };
      });
    },
    [setFormData, setIsCollapsed, isCollapsed]
  );

  const onClickGenterate = useCallback(() => {
    if (formData.macAddress === "") {
      setValidateMsg((msg) => {
        return {
          ...msg,
          macAddress: intlString(
            lang.validateMsg.help.utility.admin.macAddress.empty
          ),
          time: "",
        };
      });
      return;
    }
    if (!checkMacAddressRegex(formData.macAddress)) {
      setValidateMsg((msg) => {
        return {
          ...msg,
          macAddress: intlString(
            lang.validateMsg.help.utility.admin.macAddress.valid
          ),
          time: "",
        };
      });
      return;
    }
    if (formData.device !== "VMAX") {
      if (time === "") {
        setValidateMsg((msg) => {
          return {
            ...msg,
            time: intlString(lang.validateMsg.help.utility.admin.time.empty),
            macAddress: "",
          };
        });
        return;
      }
      if (!isValidTimeInput(time)) {
        setValidateMsg((msg) => {
          return {
            ...msg,
            time: intlString(lang.validateMsg.help.utility.admin.time.valid),
            macAddress: "",
          };
        });
        return;
      }
    }

    onInitialValidate();
    const accountExpireDate = getAccountTimeFormatDate(
      formData.expireDate,
      {
        ...selectedAccount,
        dateFormat: "YYYY-MM-DD",
      },
      false,
      false
    );
    const payload = {
      ...formData,
      expireDate:
        formData.device === "VMAX"
          ? `${accountExpireDate}T00:00:00.000`
          : `${accountExpireDate}T${time}`,
    };

    if (formData.device === "VMAX") {
      delete payload.model;
    }
    mutationGenerateOTP.mutate({
      accountId: selectedAccount.accountId,
      payload,
    });
  }, [formData, time, selectedAccount.accountId]);

  const onClickCopy = useCallback(() => {
    handleCopyClipBoard(otp, "Generated OTP");
  }, [otp]);
  useEffect(() => {
    const date = getTodayToUTCDate(selectedAccount.timezone);
    setFormData((form) => {
      return {
        ...form,
        expireDate: date,
      };
    });
  }, [selectedAccount.timezone]);

  return (
    <AdminUnlockPresenter
      isCollapsed={isCollapsed}
      formData={formData}
      otp={otp}
      time={time}
      validateMsg={validateMsg}
      onClickGenterate={onClickGenterate}
      onClickCopy={onClickCopy}
      onChange={onChange}
      timezone={selectedAccount.timezone}
      adminUnlockVersion={adminUnlockVersion.data?.result?.minVersion}
    />
  );
}
